import { useEffect } from 'react';
import { useQuery, type QueryKey, type UseQueryOptions, type UseQueryResult } from '@tanstack/react-query';
import { isTransient } from '@/api/isTransientError';
import { handleTrpcQuery } from '@/api/trpcResponseHandler';
import { TRANSIENT_ERROR_RETRY_COUNT } from '@/config/constants';

export interface AppQueryErrorConfig {
    /** Message used when the failure has no message of its own */
    fallbackErrorMessage: string;
    /** Routes the message into page state (e.g. an error banner) */
    setError?: (msg: string) => void;
    /** Shows an error toast; defaults to true inside handleTrpcQuery */
    showErrorToast?: boolean;
}

/**
 * useQuery with the app-standard transient retry policy baked in, plus
 * optional standardized error reporting via handleTrpcQuery (replacing the
 * per-page `useEffect(() => { if (q.error) handleTrpcQuery(q, ...) })`
 * blocks). Callers can still override retry or add staleTime/enabled etc.
 */
export function useAppQuery<TQueryFnData, TError, TData, TQueryKey extends QueryKey>(
    options: UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>,
    errorConfig?: AppQueryErrorConfig
): UseQueryResult<TData, TError> {
    const query = useQuery<TQueryFnData, TError, TData, TQueryKey>({
        retry: (failureCount, error) => isTransient(error) && failureCount < TRANSIENT_ERROR_RETRY_COUNT,
        ...options
    });

    const { fallbackErrorMessage, setError, showErrorToast } = errorConfig ?? {};

    // handleTrpcQuery dedupes internally by queryHash + errorUpdatedAt, so
    // re-running this effect across renders cannot double-report.
    useEffect(() => {
        if (fallbackErrorMessage === undefined || !query.error) return;

        handleTrpcQuery(query, { fallbackErrorMessage, setError, showErrorToast });
    }, [query, fallbackErrorMessage, setError, showErrorToast]);

    return query;
}
